import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { Lifecycle } from './lifecycle';
import type { Vec } from '../destruction/structure';

export interface EntityToken {
  id: string;
  epoch: number;
  generation: number;
}
export interface EntityPart {
  anchor: string;
  bounds: THREE.Box3;
}
export interface EntityRecord {
  id: string;
  name: string;
  category: string;
  kind: string;
  bounds: THREE.Box3;
  parts?: readonly EntityPart[];
  /** The same pose used for rendering; parent/container transfers may compose it. */
  pose: (anchor?: string) => THREE.Matrix4 | undefined;
  body?: RAPIER.RigidBody;
  interactable?: () => boolean;
  /** Live conservative bounds let an off-screen articulated pose stay lazy. */
  broadPhase?: { position: Vec; radius: () => number };
}
export interface EntityAttachment {
  target: EntityToken;
  anchor: string;
  local: THREE.Vector3;
  localQ: THREE.Quaternion;
}
export interface EntityHit {
  entity: EntityToken;
  anchor?: string;
  point: THREE.Vector3;
  normal: THREE.Vector3;
}
interface Entry {
  record: EntityRecord;
  token: EntityToken;
  center: THREE.Vector3;
  radius: number;
  localCenter: THREE.Vector3;
  localRadius: number;
  bounds: THREE.Box3;
  matrix?: THREE.Matrix4;
}

/** Stable identity is separate from renderer slots, physics handles and motion
 * containers. An ID reused in the same epoch gets a new generation; neither that
 * nor a reset can inherit an old attachment or interaction reference.
 */
export class EntityRegistry {
  private entries = new Map<string, Entry>();
  private generations = new Map<string, number>();
  private bodies = new WeakMap<RAPIER.RigidBody, EntityToken>();
  private rotationMatrix = new THREE.Matrix4();
  constructor(readonly life: Lifecycle) {}
  register(record: EntityRecord): EntityToken {
    const generation = (this.generations.get(record.id) ?? 0) + 1;
    this.generations.set(record.id, generation);
    const token = { id: record.id, epoch: this.life.epoch, generation };
    this.entries.set(record.id, {
      record,
      token,
      center: new THREE.Vector3(),
      radius: 1,
      localCenter: record.bounds.getCenter(new THREE.Vector3()),
      localRadius: record.bounds.min.distanceTo(record.bounds.max) * 0.5,
      bounds: record.bounds.clone(),
    });
    this.sync(record.id);
    if (record.body) this.bodies.set(record.body, token);
    return token;
  }
  unregister(id: string): void {
    this.entries.delete(id);
  }
  sync(id: string): void {
    const entry = this.entries.get(id);
    if (entry?.record.broadPhase) return;
    const matrix = entry?.record.pose();
    if (!entry || !matrix) return;
    const sameBounds = entry.bounds.equals(entry.record.bounds);
    if (sameBounds && entry.matrix?.equals(matrix)) return;
    if (!sameBounds) {
      entry.bounds.copy(entry.record.bounds);
      entry.bounds.getCenter(entry.localCenter);
      entry.localRadius = entry.bounds.min.distanceTo(entry.bounds.max) * 0.5;
    }
    entry.center.copy(entry.localCenter).applyMatrix4(matrix);
    const e = matrix.elements;
    const prior = entry.matrix?.elements;
    // Translation is the common moving-actor case. Its unchanged linear basis
    // preserves the conservative radius; standing actors need no repeated work.
    if (!sameBounds || !prior || [0, 1, 2, 4, 5, 6, 8, 9, 10].some((i) => prior[i] !== e[i]))
      entry.radius =
        entry.localRadius *
        Math.sqrt(
          Math.max(
            e[0] ** 2 + e[1] ** 2 + e[2] ** 2,
            e[4] ** 2 + e[5] ** 2 + e[6] ** 2,
            e[8] ** 2 + e[9] ** 2 + e[10] ** 2,
          ),
        );
    (entry.matrix ??= new THREE.Matrix4()).copy(matrix);
  }
  resolve(token: EntityToken): EntityRecord | undefined {
    const entry = this.entries.get(token.id);
    return this.life.valid(token.epoch) &&
      entry?.token.epoch === token.epoch &&
      entry.token.generation === token.generation
      ? entry.record
      : undefined;
  }
  forBody(body: RAPIER.RigidBody): EntityToken | undefined {
    const token = this.bodies.get(body);
    return token && this.resolve(token) ? { ...token } : undefined;
  }
  attach(
    target: EntityToken,
    point: THREE.Vector3,
    rotation: THREE.Quaternion,
    anchor = 'root',
  ): EntityAttachment | undefined {
    const matrix = this.resolve(target)?.pose(anchor);
    if (!matrix) return;
    const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().extractRotation(matrix));
    return {
      target: { ...target },
      anchor,
      local: point.clone().applyMatrix4(matrix.clone().invert()),
      localQ: q.invert().multiply(rotation),
    };
  }
  follow(attachment: EntityAttachment, position: THREE.Vector3, rotation: THREE.Quaternion): boolean {
    const matrix = this.resolve(attachment.target)?.pose(attachment.anchor);
    if (!matrix) return false;
    position.copy(attachment.local).applyMatrix4(matrix);
    rotation.setFromRotationMatrix(this.rotationMatrix.extractRotation(matrix)).multiply(attachment.localQ);
    return true;
  }
  raycast(
    origin: Vec,
    direction: Vec,
    length: number,
    exclude?: ReadonlySet<string>,
    candidates?: Iterable<string>,
  ): EntityHit | null {
    if (![origin.x, origin.y, origin.z, direction.x, direction.y, direction.z, length].every(Number.isFinite))
      return null;
    const ray = new THREE.Ray(
      new THREE.Vector3(origin.x, origin.y, origin.z),
      new THREE.Vector3(direction.x, direction.y, direction.z),
    );
    let nearest = length,
      result: EntityHit | null = null;
    const all = this.entries;
    const selected = candidates
      ? (function* () {
          for (const id of candidates) {
            const e = all.get(id);
            if (e) yield e;
          }
        })()
      : all.values();
    for (const entry of selected) {
      const { record, token } = entry;
      if (exclude?.has(token.id)) continue;
      const center = record.broadPhase?.position ?? entry.center,
        radius = record.broadPhase?.radius() ?? entry.radius;
      if (!this.life.valid(token.epoch)) continue;
      const dx = center.x - origin.x,
        dy = center.y - origin.y,
        dz = center.z - origin.z;
      const along = dx * direction.x + dy * direction.y + dz * direction.z;
      if (
        along < -radius ||
        along > nearest + radius ||
        dx * dx + dy * dy + dz * dz - Math.max(0, along) ** 2 > radius * radius
      )
        continue;
      if (record.interactable?.() === false) continue;
      const root = record.pose();
      if (!root) continue;
      for (const part of record.parts ?? [{ anchor: 'root', bounds: record.bounds }]) {
        const matrix = part.anchor === 'root' ? root : record.pose(part.anchor);
        if (!matrix) continue;
        const localRay = ray.clone().applyMatrix4(matrix.clone().invert());
        const point = localRay.intersectBox(part.bounds, new THREE.Vector3());
        if (!point) continue;
        const distances = [
          Math.abs(point.x - part.bounds.min.x),
          Math.abs(point.x - part.bounds.max.x),
          Math.abs(point.y - part.bounds.min.y),
          Math.abs(point.y - part.bounds.max.y),
          Math.abs(point.z - part.bounds.min.z),
          Math.abs(point.z - part.bounds.max.z),
        ];
        const face = distances.indexOf(Math.min(...distances));
        const normal = new THREE.Vector3();
        normal.setComponent(Math.floor(face / 2), face % 2 ? 1 : -1).transformDirection(matrix);
        point.applyMatrix4(matrix);
        const distance = point.distanceTo(ray.origin);
        if (distance > nearest) continue;
        nearest = distance;
        result = { entity: { ...token }, anchor: part.anchor, point, normal };
      }
    }
    return result;
  }
  targets(): Record<string, string | number>[] {
    const out: Record<string, string | number>[] = [];
    for (const { record, token } of this.entries.values()) {
      if (!this.life.valid(token.epoch)) continue;
      const matrix = record.pose();
      if (!matrix) continue;
      const p = record.bounds.getCenter(new THREE.Vector3()).applyMatrix4(matrix);
      out.push({
        ...token,
        name: record.name,
        category: record.category,
        kind: record.kind,
        x: p.x,
        y: p.y,
        z: p.z,
      });
    }
    return out;
  }
  clear(): void {
    this.entries.clear();
    this.generations.clear();
    this.bodies = new WeakMap();
  }
  get size(): number {
    return this.entries.size;
  }
}
